import { useSyncExternalStore } from "react";

// The cart is a map of catalog id -> quantity, kept in this browser only.
export type CartMap = Record<string, number>;

const KEY = "rw-cart";
const EVENT = "rw-cart-change";
const EMPTY: CartMap = {};
let cache: { raw: string | null; value: CartMap } = { raw: null, value: EMPTY };

function read(): CartMap {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    return EMPTY; // private mode or storage turned off: the cart just stays empty
  }
  if (raw === cache.raw) return cache.value;
  let value: CartMap = EMPTY;
  try {
    const parsed = JSON.parse(raw ?? "{}");
    if (parsed && typeof parsed === "object") value = parsed as CartMap;
  } catch {
    /* bad JSON: treat as empty */
  }
  cache = { raw, value };
  return value;
}

function write(next: CartMap) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* nothing we can do */
  }
  window.dispatchEvent(new Event(EVENT));
}

export function setQuantity(id: string, quantity: number) {
  const next = { ...read() };
  if (quantity > 0) next[id] = Math.min(Math.floor(quantity), 20);
  else delete next[id];
  write(next);
}

export function clearCart() {
  write({});
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

// The server render (and the first client render) always sees an empty cart,
// so the prerendered HTML matches; the real cart appears right after.
export const useCart = (): CartMap => useSyncExternalStore(subscribe, read, () => EMPTY);

export const cartCount = (cart: CartMap) => Object.values(cart).reduce((n, q) => n + q, 0);
